import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, ArrowRight, ShareNetwork } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>
      {pos}
    </span>
  );
}

export default function SharedTrade() {
  const { token } = useParams();
  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrade = async () => {
      try {
        const res = await axios.get(`${API}/shared-trade/${token}`);
        setTrade(res.data);
      } catch (e) {
        setError("Trade not found or link expired");
      } finally {
        setLoading(false);
      }
    };
    fetchTrade();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#ccff00] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !trade) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-4">
        <ShareNetwork size={48} weight="thin" className="text-[#333] mb-4" />
        <p className="text-[#a3a3a3] text-lg mb-2">{error || "Trade not found"}</p>
        <Link to="/" className="text-[#ccff00] text-sm hover:underline">Go to Dynasty Trade Maker</Link>
      </div>
    );
  }

  const diff = (trade.value_received || 0) - (trade.value_sent || 0);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="shared-trade-page">
      {/* Header */}
      <div className="border-b border-[#262626] bg-[#0a0a0a]">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <div className="w-7 h-7 bg-[#ccff00] flex items-center justify-center rounded-sm">
              <Trophy size={14} weight="bold" color="#000" />
            </div>
            <span className="font-heading text-xl tracking-tight text-white">
              DYNASTY TRADE<span className="text-[#ccff00]">MAKER</span>
            </span>
          </Link>
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] flex items-center gap-1">
            <ShareNetwork size={12} weight="bold" /> SHARED TRADE
          </span>
        </div>
      </div>

      {/* Trade Card */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="border border-[#262626] bg-[#171717] p-5 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-2">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight" data-testid="shared-trade-title">
                TRADE PROPOSAL
              </h1>
              {trade.league_name && (
                <p className="text-xs font-mono text-[#666] mt-1">{trade.league_name}</p>
              )}
            </div>
            <div className={`font-mono font-bold text-lg ${diff >= 0 ? 'text-[#4ade80]' : 'text-[#ff3b30]'}`}>
              {diff >= 0 ? '+' : ''}{diff.toLocaleString()} value
            </div>
          </div>

          <p className="text-xs font-semibold text-[#a3a3a3] uppercase mb-4">
            Trade with {trade.trade_partner}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {/* Send */}
            <div className="border border-[#ff3b30]/20 bg-[#ff3b30]/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff3b30] mb-3">SENDS</p>
              {(trade.send || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                  <div className="flex items-center gap-2">
                    <PosBadge pos={p.position} />
                    <span className="text-sm font-semibold text-white">{p.name}</span>
                  </div>
                  <span className="font-mono text-xs text-[#a3a3a3]">{(p.value || 0).toLocaleString()}</span>
                </div>
              ))}
              <div className="border-t border-[#262626] mt-2 pt-2 flex justify-between">
                <span className="text-[10px] font-semibold text-[#666] uppercase">Total</span>
                <span className="font-mono text-sm font-bold text-[#ff3b30]">{(trade.value_sent || 0).toLocaleString()}</span>
              </div>
            </div>

            {/* Receive */}
            <div className="border border-[#4ade80]/20 bg-[#4ade80]/5 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4ade80] mb-3">RECEIVES</p>
              {(trade.receive || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                  <div className="flex items-center gap-2">
                    <PosBadge pos={p.position} />
                    <span className="text-sm font-semibold text-white">{p.name}</span>
                  </div>
                  <span className="font-mono text-xs text-[#a3a3a3]">{(p.value || 0).toLocaleString()}</span>
                </div>
              ))}
              <div className="border-t border-[#262626] mt-2 pt-2 flex justify-between">
                <span className="text-[10px] font-semibold text-[#666] uppercase">Total</span>
                <span className="font-mono text-sm font-bold text-[#4ade80]">{(trade.value_received || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Analysis */}
          {trade.analysis && (
            <div className="bg-[#0a0a0a] border border-[#262626] p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-2">AI ANALYSIS</p>
              <p className="text-sm text-[#a3a3a3] leading-relaxed">{trade.analysis}</p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#ccff00] text-xs font-semibold uppercase tracking-wider no-underline hover:underline"
              data-testid="shared-trade-cta"
            >
              Create your own trade analysis <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
